import {
  CapacityUnit,
  LoadUnit,
  TemperatureUnit,
} from '@brt/shared-types'

export function celsiusToFahrenheit(c: number): number {
  return (c * 9 / 5) + 32
}

export function fahrenheitToCelsius(f: number): number {
  return (f - 32) * 5 / 9
}

export function convertCapacity(
  value: number,
  from: CapacityUnit,
  to: CapacityUnit,
  voltage?: number,
): number {
  if (!Number.isFinite(value)) {
    throw new Error(`Invalid capacity value: ${value}`)
  }

  let valueInAh: number

  switch (from) {
    case CapacityUnit.MAH:
      valueInAh = value / 1000
      break
    case CapacityUnit.AH:
      valueInAh = value
      break
    case CapacityUnit.WH:
      if (voltage === undefined || voltage === 0) {
        throw new Error('Voltage is required for Wh conversion')
      }
      valueInAh = value / voltage
      break
    case CapacityUnit.KWH:
      if (voltage === undefined || voltage === 0) {
        throw new Error('Voltage is required for kWh conversion')
      }
      valueInAh = (value * 1000) / voltage
      break
  }

  switch (to) {
    case CapacityUnit.MAH:
      return valueInAh * 1000
    case CapacityUnit.AH:
      return valueInAh
    case CapacityUnit.WH:
      if (voltage === undefined || voltage === 0) {
        throw new Error('Voltage is required for Wh conversion')
      }
      return valueInAh * voltage
    case CapacityUnit.KWH:
      if (voltage === undefined || voltage === 0) {
        throw new Error('Voltage is required for kWh conversion')
      }
      return (valueInAh * voltage) / 1000
  }
}

export function convertLoad(
  value: number,
  from: LoadUnit,
  to: LoadUnit,
  voltage?: number,
): number {
  if (!Number.isFinite(value)) {
    throw new Error(`Invalid load value: ${value}`)
  }

  let valueInA: number

  switch (from) {
    case LoadUnit.MA:
      valueInA = value / 1000
      break
    case LoadUnit.A:
      valueInA = value
      break
    case LoadUnit.MW:
      if (voltage === undefined || voltage === 0) {
        throw new Error('Voltage is required for mW conversion')
      }
      valueInA = (value / 1000) / voltage
      break
    case LoadUnit.W:
      if (voltage === undefined || voltage === 0) {
        throw new Error('Voltage is required for W conversion')
      }
      valueInA = value / voltage
      break
    case LoadUnit.KW:
      if (voltage === undefined || voltage === 0) {
        throw new Error('Voltage is required for kW conversion')
      }
      valueInA = (value * 1000) / voltage
      break
  }

  switch (to) {
    case LoadUnit.MA:
      return valueInA * 1000
    case LoadUnit.A:
      return valueInA
    case LoadUnit.MW:
      if (voltage === undefined || voltage === 0) {
        throw new Error('Voltage is required for mW conversion')
      }
      return valueInA * voltage * 1000
    case LoadUnit.W:
      if (voltage === undefined || voltage === 0) {
        throw new Error('Voltage is required for W conversion')
      }
      return valueInA * voltage
    case LoadUnit.KW:
      if (voltage === undefined || voltage === 0) {
        throw new Error('Voltage is required for kW conversion')
      }
      return (valueInA * voltage) / 1000
  }
}

export function convertTemperature(
  value: number,
  from: TemperatureUnit,
  to: TemperatureUnit,
): number {
  if (!Number.isFinite(value)) {
    throw new Error(`Invalid temperature value: ${value}`)
  }

  if (from === to) return value

  if (from === TemperatureUnit.CELSIUS && to === TemperatureUnit.FAHRENHEIT) {
    return celsiusToFahrenheit(value)
  }

  return fahrenheitToCelsius(value)
}

export function isCurrentBased(unit: LoadUnit): boolean {
  return unit === LoadUnit.MA || unit === LoadUnit.A
}

export function isPowerBased(unit: LoadUnit): boolean {
  return unit === LoadUnit.MW || unit === LoadUnit.W || unit === LoadUnit.KW
}

export function validateConsistentLoadDimensions(units: LoadUnit[]): boolean {
  const currentUnits = units.filter(isCurrentBased)
  const powerUnits = units.filter(isPowerBased)
  return currentUnits.length === 0 || powerUnits.length === 0
}
